import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppCheckbox from '../../common/AppCheckbox';
import { triggerNotification } from '../../../helpers/notification';

const RegenerateProductImagesModal = ({
  isOpen,
  onClose,
  hasUnsavedChanges,
  onRegenerate
}) => {
  const [formState, setFormState] = useState({
    loading: false,
    includePrintImages: false,
    includeProductImages: true
  });

  useEffect(() => {
    if (!isOpen) {
      setFormState({
        loading: false,
        includePrintImages: false,
        includeProductImages: true
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        onCancel: onClose,
        isLoading: formState.loading,
        submitTitle: 'Yes, regenerate images',
        isSubmitHidden: hasUnsavedChanges,
        isSubmitDisabled:
          !formState.includePrintImages && !formState.includeProductImages,
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          onRegenerate(
            {
              includePrintImages: formState.includePrintImages,
              includeProductImages: formState.includeProductImages
            },
            () => {
              triggerNotification('Images generated!', 'Success', 'green');
              onClose();
            },
            (error) => {
              triggerNotification(error);
              setFormState({ ...formState, loading: false });
            }
          );
        }
      }}
      isOpen={isOpen}
      onClose={onClose}
      size={700}
      title="Regenerate print images?"
    >
      {hasUnsavedChanges ? (
        <AppStack style={{ gap: 10 }}>
          <AppText>
            You have unsaved changes. Please save or discard them before
            generating images for this product.
          </AppText>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 20 }}>
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to regenerate the images for this product?
            </AppText>

            <AppText
              style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
            >
              This operation populates all design template sizes, retrieves any
              new images, and automatically aligns each variant with its
              corresponding image.
            </AppText>
          </AppStack>

          <Divider label="Regenerate options" />

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            Each selection will increase the time it takes to complete the
            regeneration process.
          </AppText>

          <AppCheckbox
            checked={formState.includePrintImages}
            description="This will regenerate the print images used for production and mockups."
            disabled={formState.loading}
            label="Print images"
            onChange={() =>
              setFormState({
                ...formState,
                includePrintImages: !formState.includePrintImages
              })
            }
            styles={{ label: { fontWeight: 500 } }}
          />
          <AppCheckbox
            checked={formState.includeProductImages}
            description="This will regenerate the product images used for display and marketing."
            disabled={formState.loading}
            label="Product images"
            onChange={() =>
              setFormState({
                ...formState,
                includeProductImages: !formState.includeProductImages
              })
            }
            styles={{ label: { fontWeight: 500 } }}
          />
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

RegenerateProductImagesModal.propTypes = {
  hasUnsavedChanges: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRegenerate: PropTypes.func
};

export default RegenerateProductImagesModal;
